import { Dialog, DialogPanel, DialogBackdrop, Transition, TransitionChild } from "@headlessui/react";
import { useState, useEffect } from "react";
import {
  Card,
  Title,
  Text,
  TextInput,
  Textarea,
  Button,
  Grid,
  Col,
  Switch,
  Flex,
  MultiSelect,
  MultiSelectItem,
  Badge,
} from "@tremor/react";
import * as oauthAPI from "../services/oauth";

const grantTypeOptions = [
  { value: "authorization_code", label: "Authorization Code" },
  { value: "client_credentials", label: "Client Credentials" },
  { value: "refresh_token", label: "Refresh Token" },
];

const defaultScopeOptions = [
  { value: "profile", label: "Profile" },
  { value: "email", label: "Email" },
];

function OAuthDrawer({ isOpen, onClose, clientId, onSave }) {
  const isNewClient = !clientId;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [redirectUris, setRedirectUris] = useState("");
  const [grants, setGrants] = useState(["authorization_code", "refresh_token"]);
  const [scope, setScope] = useState("profile");
  const [isActive, setIsActive] = useState(true);
  
  // Secrets (only shown after creation)
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [newClientId, setNewClientId] = useState("");
  
  // Error handling
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      resetForm();
      if (!isNewClient) {
        loadClient();
      }
    }
  }, [isOpen, clientId]);

  const resetForm = () => {
    if (isNewClient) {
      setName("");
      setDescription("");
      setRedirectUris("");
      setGrants(["authorization_code", "refresh_token"]);
      setScope("profile");
      setIsActive(true);
    }
    setClientSecret("");
    setShowSecret(false);
    setNewClientId("");
    setFormErrors({});
  };

  const loadClient = async () => {
    setIsLoading(true);
    try {
      const client = await oauthAPI.getOAuthClient(clientId);
      setName(client.name);
      setDescription(client.description || "");
      setRedirectUris(client.redirectUris.join("\n"));
      setGrants(client.grants);
      setScope(client.scope);
      setIsActive(client.isActive);
    } catch (error) {
      console.error("Error loading client:", error);
      setFormErrors({ form: "Failed to load client data." });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!name.trim()) {
      errors.name = "Name is required";
    }
    
    const uris = redirectUris.split("\n").filter(uri => uri.trim());
    if (uris.length === 0) {
      errors.redirectUris = "At least one redirect URI is required";
    } else {
      // Basic URL validation for each URI
      const invalidUris = uris.filter(uri => {
        try {
          new URL(uri);
          return false; // valid
        } catch (e) {
          return true; // invalid
        }
      });
      
      if (invalidUris.length > 0) {
        errors.redirectUris = "Some redirect URIs are invalid URLs";
      }
    }
    
    if (grants.length === 0) {
      errors.grants = "At least one grant type is required";
    }
    
    if (!scope.trim()) {
      errors.scope = "Scope is required";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    try {
      const clientData = {
        name,
        description,
        redirectUris: redirectUris.split("\n").filter(uri => uri.trim()),
        grants,
        scope,
        isActive,
      };
      
      let response;
      if (isNewClient) {
        response = await oauthAPI.createOAuthClient(clientData);
        setClientSecret(response.clientSecret);
        setNewClientId(response.clientId);
        setShowSecret(true);
      } else {
        response = await oauthAPI.updateOAuthClient(clientId, clientData);
        if (onSave) {
          onSave();
        }
        onClose();
      }
    } catch (error) {
      console.error("Error saving client:", error);
      setFormErrors({ form: "Failed to save client. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Transition show={isOpen}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-50"
        onClose={() => {
          if (!isSaving) onClose();
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <DialogBackdrop className="absolute inset-0 bg-black bg-opacity-30" />

          <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-2xl">
                <div className="flex flex-col h-full bg-white dark:bg-gray-900 shadow-xl">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                      {isNewClient ? "Create OAuth Client" : "Edit OAuth Client"}
                    </h2>
                  </div>
                  <div className="relative flex-1 px-6 py-6 overflow-y-auto">
                    {isLoading ? (
                      <Text>Loading...</Text>
                    ) : (
                      <form onSubmit={handleSubmit}>
                        <div className="space-y-6">
                          <Card>
                            <Grid numItemsMd={2} className="gap-6">
                              <Col>
                                <Text>Basic Information</Text>
                                <TextInput
                                  className="mt-2"
                                  label="Name"
                                  placeholder="My Application"
                                  value={name}
                                  onChange={(e) => setName(e.target.value)}
                                  error={!!formErrors.name}
                                  errorMessage={formErrors.name}
                                  required
                                />
                                <Textarea
                                  className="mt-4"
                                  label="Description"
                                  placeholder="Description of your application"
                                  value={description}
                                  onChange={(e) => setDescription(e.target.value)}
                                  rows={3}
                                />
                              </Col>
                              
                              <Col>
                                <Text>Status</Text>
                                <div className="mt-4">
                                  <Flex>
                                    <Text>Active</Text>
                                    <Switch 
                                      checked={isActive} 
                                      onChange={setIsActive} 
                                      color="green"
                                    />
                                  </Flex>
                                  <Text className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                    When inactive, this client cannot be used for authentication
                                  </Text>
                                </div>
                              </Col>
                            </Grid>
                          </Card>
                          
                          <Card>
                            <Text>Redirect URIs</Text>
                            <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Enter one redirect URI per line. These are the allowed URLs that your application can redirect to after authentication.
                            </Text>
                            <Textarea
                              className="mt-2"
                              placeholder="https://example.com/callback"
                              value={redirectUris}
                              onChange={(e) => setRedirectUris(e.target.value)}
                              error={!!formErrors.redirectUris}
                              errorMessage={formErrors.redirectUris}
                              rows={4}
                              required
                            />
                          </Card>
                          
                          <Card>
                            <Text>OAuth Configuration</Text>
                            <div className="mt-4">
                              <Text className="mb-2">Grant Types</Text>
                              <MultiSelect
                                value={grants}
                                onValueChange={setGrants}
                                placeholder="Select grant types"
                                className="max-w-md"
                                error={!!formErrors.grants}
                                errorMessage={formErrors.grants}
                              >
                                {grantTypeOptions.map((option) => (
                                  <MultiSelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MultiSelectItem>
                                ))}
                              </MultiSelect>
                            </div>
                            
                            <div className="mt-4">
                              <Text className="mb-2">Default Scope</Text>
                              <TextInput
                                placeholder="profile email"
                                value={scope}
                                onChange={(e) => setScope(e.target.value)}
                                error={!!formErrors.scope}
                                errorMessage={formErrors.scope}
                                className="max-w-md"
                              />
                              <div className="mt-2 flex gap-2">
                                {defaultScopeOptions.map((option) => (
                                  <Badge 
                                    key={option.value} 
                                    color="blue"
                                    className="cursor-pointer"
                                    onClick={() => setScope(
                                      scope.includes(option.value) 
                                        ? scope.replace(option.value, '').replace(/\s+/g, ' ').trim()
                                        : (scope + ' ' + option.value).trim()
                                    )}
                                  >
                                    {option.label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </Card>
                          
                          {showSecret && clientSecret && (
                            <Card className="border-2 border-yellow-500">
                              <Title className="text-yellow-700">Client Credentials</Title>
                              <Text className="mt-2 font-semibold">
                                Save these credentials securely. The client secret will not be shown again.
                              </Text>
                              
                              <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-md">
                                <div className="mb-4">
                                  <Text className="text-sm font-semibold">Client ID</Text>
                                  <div className="flex items-center mt-1">
                                    <code className="bg-white dark:bg-gray-900 p-2 rounded border flex-grow">{newClientId}</code>
                                    <Button
                                      variant="light"
                                      size="xs"
                                      className="ml-2"
                                      onClick={() => navigator.clipboard.writeText(newClientId)}
                                    >
                                      Copy
                                    </Button>
                                  </div>
                                </div>
                                
                                <div>
                                  <Text className="text-sm font-semibold">Client Secret</Text>
                                  <div className="flex items-center mt-1">
                                    <code className="bg-white dark:bg-gray-900 p-2 rounded border flex-grow">
                                      {clientSecret}
                                    </code>
                                    <Button
                                      variant="light"
                                      size="xs"
                                      className="ml-2"
                                      onClick={() => navigator.clipboard.writeText(clientSecret)}
                                    >
                                      Copy
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          )}
                          
                          {formErrors.form && (
                            <Text color="red">
                              {formErrors.form}
                            </Text>
                          )}
                        </div>
                      </form>
                    )}
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                    {showSecret ? (
                      <Button onClick={() => { 
                        onSave && onSave();
                        onClose();
                      }} className="w-full justify-center">
                        Done
                      </Button>
                    ) : (
                      <Flex justifyContent="between">
                        <Button
                          onClick={onClose}
                          variant="secondary"
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSubmit}
                          loading={isSaving}
                        >
                          {isNewClient ? "Create Client" : "Update Client"}
                        </Button>
                      </Flex>
                    )}
                  </div>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default OAuthDrawer;